import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ChevronDown, ChevronUp, Loader2, CheckCircle2, Info } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API } from '../../lib/apiBase';
import { safeJson } from '../../lib/safeJson';

const h = () => {
  const t = localStorage.getItem('hrms-token');
  return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

function GuidelineBox({ param }) {
  return (
    <div className="mt-2 rounded-md bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 p-3 text-xs"
      data-testid={`guideline-${param.key}`}>
      <p className="font-semibold flex items-center gap-1 mb-2 text-slate-700 dark:text-slate-200">
        <Info className="w-3.5 h-3.5" /> Evaluation guidelines
      </p>
      {param.items && (
        <ol className="list-decimal ml-4 mb-2 space-y-0.5 text-slate-600 dark:text-slate-300">
          {param.items.map((it, i) => <li key={i}>{it}</li>)}
        </ol>
      )}
      <table className="w-full">
        <tbody>
          {param.guideline.map((g, i) => (
            <tr key={i} className="border-t border-slate-200 dark:border-slate-700">
              <td className="py-1 pr-3 text-slate-600 dark:text-slate-300">{g[0]}</td>
              <td className="py-1 text-right font-semibold whitespace-nowrap">{g[1]} pts</td>
            </tr>
          ))}
        </tbody>
      </table>
      {param.note && <p className="mt-2 italic text-slate-500">{param.note}</p>}
    </div>
  );
}

export default function EvaluationDialog({ form, open, onClose, onDone, readOnly = false }) {
  const [params, setParams] = useState([]);
  const [meta, setMeta] = useState({ params: [], max_points: 700, eligible_cutoff: 420 });
  const [scores, setScores] = useState({});
  const [remarks, setRemarks] = useState({});
  const [overall, setOverall] = useState('');
  const [unsat, setUnsat] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const res = await fetch(`${API}/api/faculty-appraisal/meta`, { headers: h(), credentials: 'include' });
      if (res.ok) {
        const d = await safeJson(res);
        setMeta(d);
        setParams(d.params || []);
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open || !form) return;
    const ev = form.evaluation || {};
    const s = {}, r = {};
    (ev.params || []).forEach(p => { if (p.score != null) s[p.key] = String(p.score); if (p.remark) r[p.key] = p.remark; });
    setScores(s); setRemarks(r);
    setOverall(ev.overall_remark || '');
    setUnsat(ev.unsatisfactory_reasons || '');
  }, [open, form]);

  const total = useMemo(() => params.reduce(
    (sum, p) => sum + (scores[p.key] ? Number(scores[p.key]) * p.factor : 0), 0), [params, scores]);
  const scoredCount = params.filter(p => scores[p.key] !== undefined && scores[p.key] !== '').length;
  const cutoff = meta.eligible_cutoff || 420;
  const maxPoints = meta.max_points || 700;

  if (!form) return null;

  const submit = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/faculty-appraisal/forms/${form.form_id}/evaluate`, {
        method: 'PUT', headers: h(), credentials: 'include',
        body: JSON.stringify({
          params: params.map(p => ({ key: p.key, score: scores[p.key] ?? '', remark: remarks[p.key] || '' })),
          overall_remark: overall, unsatisfactory_reasons: unsat,
        }),
      });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Failed');
      toast.success(`Evaluation saved — ${d.total_points}/${maxPoints}${d.eligible ? ' (eligible)' : ''}`);
      onDone?.();
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="evaluation-dialog">
        <DialogHeader>
          <DialogTitle>Part II — {form.is_hod ? 'Director' : 'HOD'} Evaluation · {form.employee_name}</DialogTitle>
          <DialogDescription>
            {form.department} · A.Y. {form.academic_year} · Score each parameter on a 5-point scale.
            Minimum {cutoff} / {maxPoints} makes the faculty eligible for recommendation.
          </DialogDescription>
        </DialogHeader>

        {form.evaluation_locked && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="eval-locked-note">
            Locked on submission{form.evaluation_locked_by ? ` by ${form.evaluation_locked_by}` : ''}
            {form.evaluation_locked_at ? ` (${form.evaluation_locked_at.slice(0, 10)})` : ''}.
            Scores cannot be changed unless the Principal / Super Admin unlocks this evaluation.
          </div>
        )}

        <div className="sticky top-0 z-10 flex items-center justify-between rounded-md bg-slate-900 text-white px-4 py-3" data-testid="eval-total-bar">
          <div>
            <p className="text-xs text-slate-300">Total Points Obtained</p>
            <p className="text-2xl font-bold">{total.toFixed(0)} <span className="text-sm font-normal text-slate-400">/ {maxPoints}</span></p>
          </div>
          <Badge className={total >= cutoff ? 'bg-emerald-500' : 'bg-red-500'} data-testid="eval-eligibility">
            {total >= cutoff ? 'Eligible for recommendation' : `Below minimum ${cutoff}`}
          </Badge>
        </div>

        <div className="space-y-2 mt-3">
          {params.map(p => {
            const score = scores[p.key];
            const pts = score ? Number(score) * p.factor : 0;
            return (
              <div key={p.key} className="rounded-md border border-slate-200 dark:border-slate-700 p-3" data-testid={`eval-param-${p.key}`}>
                <div className="flex flex-col md:flex-row md:items-center gap-3">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{p.sn}. {p.label}</p>
                    <p className="text-xs text-slate-400">Multiplying factor: {p.factor} · Max {p.factor * 5} pts</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={score || ''} disabled={readOnly}
                      onValueChange={v => setScores(s => ({ ...s, [p.key]: v }))}>
                      <SelectTrigger className="w-28 h-9" data-testid={`score-${p.key}`}>
                        <SelectValue placeholder="Score" />
                      </SelectTrigger>
                      <SelectContent>
                        {['0', '1', '2', '3', '4', '5'].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <div className="w-16 text-right font-bold text-sm" data-testid={`points-${p.key}`}>{pts}</div>
                    <Button type="button" variant="ghost" size="sm" className="h-8 px-2"
                      onClick={() => setExpanded(expanded === p.key ? null : p.key)}
                      data-testid={`toggle-guideline-${p.key}`}>
                      {expanded === p.key ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                {expanded === p.key && <GuidelineBox param={p} />}
                {!readOnly && (
                  <Input className="mt-2 h-8 text-xs" placeholder="Remark (optional)"
                    value={remarks[p.key] || ''} onChange={e => setRemarks(r => ({ ...r, [p.key]: e.target.value }))}
                    data-testid={`remark-${p.key}`} />
                )}
                {readOnly && remarks[p.key] && <p className="mt-1 text-xs text-slate-500">{remarks[p.key]}</p>}
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
          <div className="space-y-1">
            <Label className="text-xs">Overall Remark</Label>
            <Textarea rows={3} value={overall} disabled={readOnly} onChange={e => setOverall(e.target.value)} data-testid="eval-overall-remark" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Reasons for Unsatisfactory Rating (if any)</Label>
            <Textarea rows={3} value={unsat} disabled={readOnly} onChange={e => setUnsat(e.target.value)} data-testid="eval-unsat-reasons" />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700 mt-4">
          {!readOnly && (
            <p className="text-xs text-slate-500 mr-auto" data-testid="eval-progress">
              {scoredCount} of {params.length} parameters scored
            </p>
          )}
          <Button variant="outline" onClick={onClose} data-testid="eval-close">Close</Button>
          {!readOnly && (
            <Button onClick={submit} disabled={busy || scoredCount < params.length} data-testid="eval-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              Confirm Evaluation &amp; Send to Director
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
